/**
 * Live Scalp Localize export helpers (S4).
 * PNG + metadata clipboard with IFU footnote — adjunct only.
 */

import {
  formatLiveTopographyBufferSemantics,
  formatLiveTopographyScaleSemantics,
  formatLiveTopographyTimeSemantics,
  type LiveTopographyPreviewModel,
} from "./eegViewerLiveTopography";
import {
  LIVE_SCALP_IFU,
  formatLiveScalpMaxFocus,
  type LiveScalpBandKey,
  type LiveScalpLocalizeField,
  type LiveScalpMaxFocus,
  type LiveScalpViewMode,
} from "./liveScalpLocalizeHelpers";

export type LiveScalpExportMetadataInput = {
  preview: LiveTopographyPreviewModel;
  viewMode: LiveScalpViewMode;
  localizeField: LiveScalpLocalizeField;
  bandKey?: LiveScalpBandKey;
  montageLabel: string;
  maxFocus: LiveScalpMaxFocus | null;
  eventHintLabel?: string | null;
};

export type LiveScalpExportMetadata = {
  title: string;
  generatedAtIso: string;
  timeSemantics: string;
  scaleSemantics: string;
  bufferSemantics: string | null;
  montageLabel: string;
  maxFocusLabel: string | null;
  fieldMode: string;
  ifuBadge: string;
  ifuSummary: string;
  ifuIntendedUse: string;
  ifuExcludedUse: string;
  plaintext: string;
  filenameSafeStamp: string;
};

export function buildLiveScalpExportMetadata(
  input: LiveScalpExportMetadataInput,
): LiveScalpExportMetadata {
  const generatedAtIso = new Date().toISOString();
  const fieldMode =
    input.viewMode === "live"
      ? "live-instant"
      : input.localizeField === "band"
        ? `localize-band-${input.bandKey ?? "alpha"}`
        : `localize-${input.localizeField}`;
  const timeSemantics = formatLiveTopographyTimeSemantics(input.preview);
  const scaleSemantics = formatLiveTopographyScaleSemantics(input.preview);
  const bufferSemantics = formatLiveTopographyBufferSemantics(input.preview);
  const maxFocusLabel = formatLiveScalpMaxFocus(input.maxFocus);
  const title = `NerveDX Live Scalp · ${fieldMode}`;
  const filenameSafeStamp = generatedAtIso
    .replace(/[:.]/g, "-")
    .replace("T", "_")
    .slice(0, 19);

  const plaintext = [
    title,
    `Generated: ${generatedAtIso}`,
    `Mode: ${fieldMode}`,
    `Time: ${timeSemantics}`,
    `Scale: ${scaleSemantics}`,
    bufferSemantics ? `Buffer: ${bufferSemantics}` : null,
    `Montage: ${input.montageLabel}`,
    maxFocusLabel ? `Focus: ${maxFocusLabel}` : null,
    input.eventHintLabel ? `Event: ${input.eventHintLabel}` : null,
    "",
    `IFU (${LIVE_SCALP_IFU.badge}): ${LIVE_SCALP_IFU.summary}`,
    `Intended use: ${LIVE_SCALP_IFU.intendedUse}`,
    `Excluded use: ${LIVE_SCALP_IFU.excludedUse}`,
  ]
    .filter((line): line is string => line != null)
    .join("\n");

  return {
    title,
    generatedAtIso,
    timeSemantics,
    scaleSemantics,
    bufferSemantics,
    montageLabel: input.montageLabel,
    maxFocusLabel,
    fieldMode,
    ifuBadge: LIVE_SCALP_IFU.badge,
    ifuSummary: LIVE_SCALP_IFU.summary,
    ifuIntendedUse: LIVE_SCALP_IFU.intendedUse,
    ifuExcludedUse: LIVE_SCALP_IFU.excludedUse,
    plaintext,
    filenameSafeStamp,
  };
}

export function buildLiveScalpExportFilename(
  metadata: LiveScalpExportMetadata,
): string {
  const mode = metadata.fieldMode.replace(/[^a-z0-9-]+/gi, "-").toLowerCase();
  return `live-scalp-${mode}-${metadata.filenameSafeStamp}.png`;
}

/** Composite map canvas with scale + IFU footnote for clinical export. */
export function compositeLiveScalpExportCanvas(
  source: HTMLCanvasElement,
  metadata: LiveScalpExportMetadata,
): HTMLCanvasElement {
  const footerHeight = 54;
  const canvas = document.createElement("canvas");
  canvas.width = source.width;
  canvas.height = source.height + footerHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return source;
  }

  ctx.fillStyle = "#0b1522";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(source, 0, 0);

  ctx.fillStyle = "rgba(15, 28, 42, 0.98)";
  ctx.fillRect(0, source.height, canvas.width, footerHeight);
  ctx.fillStyle = "rgba(226, 236, 247, 0.95)";
  ctx.font = "11px sans-serif";
  ctx.fillText(metadata.scaleSemantics, 8, source.height + 16);
  ctx.fillText(metadata.montageLabel, 8, source.height + 32);
  ctx.fillStyle = "rgba(186, 210, 230, 0.9)";
  ctx.font = "10px sans-serif";
  const footnote = `${metadata.ifuBadge} · ${metadata.ifuSummary}`;
  ctx.fillText(
    footnote.length > 90 ? `${footnote.slice(0, 87)}…` : footnote,
    8,
    source.height + 48,
  );

  return canvas;
}

export async function canvasToPngBlob(
  canvas: HTMLCanvasElement,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/png");
  });
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  return false;
}

export async function copyPngBlobToClipboard(blob: Blob): Promise<boolean> {
  try {
    if (
      typeof ClipboardItem !== "undefined" &&
      navigator.clipboard?.write
    ) {
      await navigator.clipboard.write([
        new ClipboardItem({ "image/png": blob }),
      ]);
      return true;
    }
  } catch {
    /* unsupported */
  }
  return false;
}
